import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/react-hooks';
import dayjs from 'dayjs';
import { Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useFeatures } from 'flagged';

import type { RootState } from '../../store/store';
import { name as gameFacetSelector, setPage } from '../../store/reducers/gameFacet';
import { useDebouncedEffect } from '../../hooks';
import { listGames } from './apiQueries';
import { Game, StringFilterInput, USE_DRM } from '../../utils/types';
import GamesList from './GamesList';
import GamesFilters from './GamesFilters';
import { getCurrentDateString } from '../../utils/dateTransform';
import awsConfig from '../../aws-exports';

const PAGE_SIZE = 30;
const SORT_DIRECTION = 'DESC';
const AUTO_REFETCH_PERIOD = 30 * 1000; // 30 seconds in milliseconds
const LIMIT = 500;

const Games: FC = () => {
	const dispatch = useDispatch();

	const { videoOptimisation: isVideoOptimisation } = useFeatures();

	const { accountName } = awsConfig;
	const useDrm = !isVideoOptimisation // DRM off if videoOptimisation feature flag is false
		? USE_DRM.NO
		: accountName.includes('peacock') // DRM on if peacock account
			? USE_DRM.YES
			: accountName === 'development' // DRM both if development account
				? USE_DRM.BOTH
				: USE_DRM.NO;

	// All these search facets come from the gameFacetSelector in the redux store
	const { id, title, sport, startTime, endTime, hasAirings, status, league, page, count, autoRefetch, faceted } = useSelector(
		(storedState: RootState) => storedState[gameFacetSelector]
	);

	// debouncedId and debouncedTitle apply a 500ms pause on keyboard input of id and title fields
	// initialised with gameFacetSelector props to avoid debouncing page reload
	const [debouncedId, setDebouncedId] = useDebouncedEffect(id || '', 500);
	const [debouncedTitle, setDebouncedTitle] = useDebouncedEffect(title || '', 500);

	// on update set the debounced values
	useEffect(() => {
		setDebouncedId(id);
		setDebouncedTitle(title);
	}, [id, title, setDebouncedId, setDebouncedTitle]);

	// Set up query filters
	const filters: GameFiltersInput = {};
	if (debouncedId) filters.pk = { beginsWith: `game#${debouncedId}` }; // note: debouncedId
	if (sport) filters.sport = { beginsWith: sport };
	if (debouncedTitle) filters.title = { contains: debouncedTitle }; // note: debouncedTitle

	const calcEndTime =
		startTime && endTime && dayjs(startTime).isBefore(dayjs(endTime).add(1, 'day')) ? dayjs(endTime).add(1, 'day').format('YYYY-MM-DD') : '';

	// The main query hook
	const { loading, error, data, refetch, startPolling, stopPolling } = useQuery(listGames, {
		variables: {
			startTime,
			endTime: calcEndTime,
			nextToken: null,
			limit: LIMIT,
			sortDirection: SORT_DIRECTION,
			filter: Object.keys(filters).length > 0 ? filters : null
		},
		pollInterval: autoRefetch ? AUTO_REFETCH_PERIOD : 0
	});

	// Configure autoRefetch on the useQuery hook
	useEffect(() => {
		if (autoRefetch) {
			startPolling(AUTO_REFETCH_PERIOD);
		} else {
			stopPolling();
		}
	}, [startPolling, stopPolling, autoRefetch]);

	// Requery when filters update
	useEffect(() => {
		// To prevent false triggering we have to compose the filters again, inside useEffect
		const ueFilters: GameFiltersInput = {};
		if (debouncedId) ueFilters.pk = { beginsWith: `game#${debouncedId}` }; // note: debouncedId
		if (sport) ueFilters.sport = { beginsWith: sport };
		if (debouncedTitle) ueFilters.title = { contains: debouncedTitle }; // note: debouncedTitle

		refetch({
			startTime,
			endTime: calcEndTime,
			nextToken: null,
			limit: LIMIT,
			sortDirection: SORT_DIRECTION,
			filter: Object.keys(ueFilters).length > 0 ? ueFilters : null
		});
	}, [startTime, calcEndTime, refetch, debouncedId, sport, debouncedTitle]);

	const nextToken = data?.listGames.nextToken || null;

	const handlePageChange = (newPage: number): void => {
		dispatch(setPage(newPage));
	};

	const renderGameList = (): JSX.Element => {
		if (loading) return <Typography>Loading...</Typography>;
		if (error) return <Typography>Error loading data from the server</Typography>;

		const filteredGames = data?.listGames.items.filter((game: Game) => {
			if (hasAirings && game.airings && game.airings?.items.length === 0) return false;
			if (status && game.status !== status) return false;
			if (league && game.league?.name !== league) return false;
			return true;
		});

		if (!filteredGames || (filteredGames.length === 0 && !nextToken)) return <Typography>No games found</Typography>;

		filteredGames.sort((a: Game, b: Game) => {
			if (!a.startTime) return -1;
			if (!b.startTime) return 1;
			return dayjs(a!.startTime).isBefore(dayjs(b!.startTime)) ? 1 : -1;
		});

		const currentPage = filteredGames.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

		return (
			<GamesList
				view={startTime === getCurrentDateString() ? 'TODAY' : 'ALL'}
				pageSize={PAGE_SIZE}
				page={page}
				games={currentPage}
				count={count || filteredGames.length}
				onPageChange={handlePageChange}
				refetch={refetch}
				useDrm={useDrm}
			/>
		);
	};

	return (
		<>
			<GamesFilters />
			{!loading && nextToken && faceted && (
				<Alert severity='warning' style={{ marginTop: -10, marginBottom: 10 }}>
					Please consider narrowing your search, which is slow and too broad for accurate paginated display.
				</Alert>
			)}
			{renderGameList()}
		</>
	);
};

type GameFiltersInput = {
	pk?: StringFilterInput | null;
	sport?: StringFilterInput | null;
	title?: StringFilterInput | null;
};

export default Games;
